import type { Cart, CartItem } from "@/lib/checkout/checkout-types";
import { packTotalCents, type CatalogPiece } from "@/lib/host-panel/catalog-types";

/**
 * The e-commerce parameters of the bag and the checkout events (INST §08 and §10), built from the
 * cart as the server holds it.
 *
 * **Nothing is recalculated here.** `value` is the cart's `total` as the server answered it, read
 * as a number in reais, and each item's `price` is the server's own `unitPrice`. The backend still
 * miscounts the total of bundle items (items 33 and 34 of the host panel contract), and that is
 * the backend's to fix: a figure the front computed to hide it would be a second source of truth.
 *
 * `item_id` is the SKU. The cart does not carry it yet (item 41 of the same contract); until it
 * does, the product's id stands in, and the day the cart brings `sku` it takes over by itself.
 */

export type AnalyticsItem = {
  item_id: string;
  item_name: string;
  item_category?: string;
  /** `pack_<n>` for a bundle; absent for a single piece. */
  item_variant?: string;
  /** The server's price for the line, in reais. */
  price: number;
  quantity: number;
  /** The card's place in the list it was drawn in, from 0. */
  index?: number;
  item_list_name?: string;
};

export type EcommercePayload = {
  currency: "BRL";
  /** The server's total in reais; absent when it is not known. */
  value?: number;
  items: readonly AnalyticsItem[];
  /** Only on `purchase`. */
  transaction_id?: string;
};

/** The events the container's e-commerce reads: each one is preceded by a clean `ecommerce: null`. */
export const ECOMMERCE_EVENTS: ReadonlySet<string> = new Set([
  "view_item_list",
  "select_item",
  "view_item",
  "add_to_cart",
  "view_cart",
  "begin_checkout",
  "add_payment_info",
  "purchase",
]);

/** A decimal string from the server as a number; `undefined` for anything that is not one. */
export function reaisFromServer(value: string | null | undefined): number | undefined {
  if (value === null || value === undefined || value.trim() === "") return undefined;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * A bag line as an item. `price` is **the pack's total** (INST §07 and §10: "valor de item é sempre o
 * total do pack, com `pack_size` ao lado, e nunca o preço unitário"), and `quantity` counts packs. The
 * cart's `unitPrice` is the price of **one piece** (document 19, item 33), so a bundle's pack total is
 * that price times `bundleMultiple`: a product of two numbers the server sent, in integer cents so no
 * float drifts. The line's own `totalPrice` is not used for the item: the backend still miscounts it
 * for bundles (items 33 and 34).
 */
export function toAnalyticsItem(item: CartItem): AnalyticsItem {
  const unit = reaisFromServer(item.unitPrice) ?? 0;
  const pieces = item.isBundle ? Math.max(item.bundleMultiple, 1) : 1;

  return {
    item_id: item.sku ?? item.productId,
    item_name: item.productName,
    ...(item.isBundle ? { item_variant: `pack_${item.bundleMultiple}` } : {}),
    price: Math.round(unit * 100 * pieces) / 100,
    quantity: item.quantity,
  };
}

/**
 * A catalog piece as an item. `price` is the **pack's total** (INST §10: never the unit price) in
 * reais, and `quantity` is one pack: what a "Presentear" adds. `item_id` is the product's id until
 * the SKU is available outside the cart too.
 */
export function pieceToAnalyticsItem(
  piece: Pick<CatalogPiece, "id" | "name" | "category" | "priceCents" | "pack">,
  position?: { index: number; listName: string },
): AnalyticsItem {
  return {
    item_id: piece.id,
    item_name: piece.name,
    ...(piece.category === "" ? {} : { item_category: piece.category }),
    ...(piece.pack > 1 ? { item_variant: `pack_${piece.pack}` } : {}),
    price: packTotalCents(piece) / 100,
    quantity: 1,
    ...(position === undefined ? {} : { index: position.index, item_list_name: position.listName }),
  };
}

/** The piece a page is about, as an item: the pack's total in reais (unit price times the pack). */
export function productToAnalyticsItem(product: {
  productId: string;
  name: string;
  price: number;
  pack: number;
}): AnalyticsItem {
  // No category: the page knows the piece's collection, not its category, and the two are not the
  // same thing — the catalog's items carry the category, and the id joins them.
  return {
    item_id: product.productId,
    item_name: product.name,
    ...(product.pack > 1 ? { item_variant: `pack_${product.pack}` } : {}),
    price: product.price * Math.max(product.pack, 1),
    quantity: 1,
  };
}

export function cartEcommerce(cart: Pick<Cart, "items" | "total">): EcommercePayload {
  const value = reaisFromServer(cart.total);

  return {
    currency: "BRL",
    ...(value === undefined ? {} : { value }),
    items: cart.items.map(toAnalyticsItem),
  };
}

/**
 * The bag as it was when the person set out to pay: what the `purchase` reports, because by the
 * time the order is PAID the cart has been converted and is empty (and a Pix is paid later, in
 * another screen).
 *
 * Kept in the tab's `sessionStorage` as well as in memory (R-9): a Pix is often paid after the page
 * is reloaded, and a snapshot that lived only in memory would be gone, so the `purchase` would leave
 * with no items and no value. It holds what the `purchase` carries and nothing else: product ids and
 * names, prices, quantities, the list's id and whether a message exists (never its text), so no
 * personal data. It is cleared once the `purchase` has been reported, and the tab's end clears it too.
 */
export type CheckoutSnapshot = {
  ecommerce: EcommercePayload;
  listId: string;
  hasMessage: boolean;
};

const SNAPSHOT_STORAGE_KEY = "tb-checkout-snapshot";

let snapshot: CheckoutSnapshot | null = null;

function isStoredSnapshot(value: unknown): value is CheckoutSnapshot {
  if (typeof value !== "object" || value === null) return false;

  const candidate = value as Partial<CheckoutSnapshot>;

  return (
    typeof candidate.listId === "string" &&
    typeof candidate.hasMessage === "boolean" &&
    typeof candidate.ecommerce === "object" &&
    candidate.ecommerce !== null &&
    candidate.ecommerce.currency === "BRL" &&
    Array.isArray(candidate.ecommerce.items)
  );
}

function readStoredSnapshot(): CheckoutSnapshot | null {
  try {
    const raw = window.sessionStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);

    return isStoredSnapshot(parsed) ? parsed : null;
  } catch {
    // Storage blocked or the entry unreadable: the snapshot is simply not there.
    return null;
  }
}

function writeStoredSnapshot(next: CheckoutSnapshot | null): void {
  try {
    if (next === null) window.sessionStorage.removeItem(SNAPSHOT_STORAGE_KEY);
    else window.sessionStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the in-memory copy still serves this page.
  }
}

/** Remembers a bag that has something in it; an empty or converted cart never replaces it. */
export function rememberCheckoutSnapshot(cart: Cart | null): void {
  if (cart === null || cart.items.length === 0) return;

  snapshot = {
    ecommerce: cartEcommerce(cart),
    listId: cart.giftListId,
    hasMessage: (cart.giftMessage ?? "").trim() !== "",
  };
  writeStoredSnapshot(snapshot);
}

/** The bag set aside for the `purchase`: this page's, or the one a reload left in the tab's storage. */
export function getCheckoutSnapshot(): CheckoutSnapshot | null {
  if (snapshot !== null) return snapshot;
  if (typeof window === "undefined") return null;

  snapshot = readStoredSnapshot();

  return snapshot;
}

/** Called once the `purchase` has been reported: the bag it described is gone. */
export function clearCheckoutSnapshot(): void {
  snapshot = null;
  if (typeof window !== "undefined") writeStoredSnapshot(null);
}

/** For tests: a fresh page, with the memory and the tab's storage both empty. */
export function resetCheckoutSnapshot(): void {
  clearCheckoutSnapshot();
}

/** For tests: a page reloaded in the same tab, which keeps the storage and loses the memory. */
export function dropCheckoutSnapshotMemory(): void {
  snapshot = null;
}
